import React, { useEffect, useState } from "react";
import { Newspaper, X } from "lucide-react";
import { useScene } from "./PodcastLayer";

const chip = "h-11 px-3 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur transition-colors inline-flex items-center gap-1.5 text-xs font-medium";

function useClock() {
  const [t, setT] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setT(new Date()), 15000); return () => clearInterval(i); }, []);
  return t.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export const newsLift = (n) => (n?.ticker ? 36 : 0) + (n?.lower?.name ? 64 : 0);

export function NewsBug({ room }) {
  const n = useScene(room).news || {};
  const clock = useClock();
  if (!n.bug) return null;
  return (
    <div className="flex items-center gap-1.5 shrink-0 pointer-events-none" data-testid="news-bug">
      <span className="bg-red-600 text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-1 rounded-sm">Live</span>
      <span className="bg-black/70 text-xs tabular-nums px-2 py-1 rounded-sm" data-testid="news-clock">{clock}</span>
      <img src="/brand/logo-mark.png" alt="" className="hidden sm:block w-7 h-7 object-contain drop-shadow" />
    </div>
  );
}

// Broadcast-style graphics: name/title lower third and scrolling headline ticker (the LIVE bug sits in the top bar).
export function NewsOverlay({ room }) {
  const n = useScene(room).news || {};
  const heads = (n.ticker || "").split("\n").map((x) => x.trim()).filter(Boolean);
  return (
    <>
      {n.lower?.name && <div className="absolute left-0 z-[21] flex items-stretch pointer-events-none max-w-[92%] animate-in slide-in-from-left duration-500" style={{ bottom: n.ticker ? 40 : 6 }} data-testid="news-lower-third">
        <span className="w-1.5 bg-[var(--gold)]" />
        <div className="bg-black/80 backdrop-blur px-4 py-2"><p className="font-serif text-lg leading-tight" data-testid="news-lower-name">{n.lower.name}</p>{n.lower.title && <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--gold)]" data-testid="news-lower-title">{n.lower.title}</p>}</div>
      </div>}
      {heads.length > 0 && <div className="absolute inset-x-0 bottom-0 z-[21] h-9 flex items-center bg-[#0d0b09]/95 border-t border-[var(--gold)]/60 overflow-hidden pointer-events-none" data-testid="news-ticker">
        <span className="shrink-0 h-full px-3 flex items-center bg-[var(--gold)] text-black text-[11px] font-bold uppercase tracking-wider">Headlines</span>
        <div className="flex-1 overflow-hidden"><p className="whitespace-nowrap text-sm inline-block pl-[100%] animate-[news-scroll_linear_infinite]" style={{ animationDuration: `${Math.max(14, heads.join(" ").length / 6)}s` }}>{heads.join("   •   ")}</p></div>
      </div>}
    </>
  );
}

export function NewsTools({ room, hostName = "" }) {
  const scene = useScene(room);
  const n = scene.news || {};
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(n.lower?.name || hostName);
  const [title, setTitle] = useState(n.lower?.title || "");
  const [ticker, setTicker] = useState(n.ticker || "");
  const set = (patch) => room.scene({ ...(room.sceneNow || {}), news: { ...((room.sceneNow || {}).news || {}), ...patch } });
  const on = n.bug || n.lower?.name || n.ticker;
  const btn = "px-3 py-1.5 rounded-full text-xs";
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} className={`${chip} ${on ? "!bg-[var(--gold)]" : ""}`} data-testid="news-tools-btn"><Newspaper className="w-4 h-4" /><span className="hidden sm:inline">News</span></button>
      {open && <div className="absolute z-40 top-12 left-0 w-72 max-w-[85vw] bg-black/90 backdrop-blur-xl rounded-2xl border border-white/10 p-3 space-y-3 text-white" data-testid="news-tools-menu">
        <div className="flex items-center justify-between"><p className="text-[10px] uppercase tracking-wider text-[var(--gold)]">News overlay</p><button onClick={() => setOpen(false)} aria-label="Close"><X className="w-4 h-4" /></button></div>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!n.bug} onChange={(e) => set({ bug: e.target.checked })} data-testid="news-bug-toggle" /> LIVE tag with clock and logo</label>
        <div className="space-y-1.5"><p className="text-xs">Name banner</p>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} placeholder="Name" className="w-full bg-white/10 rounded-lg px-2 py-1.5 text-xs outline-none" data-testid="news-lower-name-input" />
          <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={80} placeholder="Title, e.g. Wedding planner, Austin" className="w-full bg-white/10 rounded-lg px-2 py-1.5 text-xs outline-none" data-testid="news-lower-title-input" />
          <div className="flex gap-1.5"><button onClick={() => name.trim() && set({ lower: { name: name.trim(), title: title.trim() } })} className={`${btn} bg-[var(--gold)]`} data-testid="news-lower-show">Show</button>
            <button onClick={() => set({ lower: null })} className={`${btn} bg-white/10`} data-testid="news-lower-hide">Hide</button></div></div>
        <div className="space-y-1.5"><p className="text-xs">Headline ticker (one per line)</p>
          <textarea value={ticker} onChange={(e) => setTicker(e.target.value)} rows={3} maxLength={600} placeholder={"Voting for the $25K Showcase closes Friday\nNew vendors join every week"} className="w-full bg-white/10 rounded-lg px-2 py-1.5 text-xs outline-none" data-testid="news-ticker-input" />
          <div className="flex gap-1.5"><button onClick={() => ticker.trim() && set({ ticker: ticker.trim() })} className={`${btn} bg-[var(--gold)]`} data-testid="news-ticker-show">Run ticker</button>
            <button onClick={() => set({ ticker: "" })} className={`${btn} bg-white/10`} data-testid="news-ticker-hide">Stop</button></div></div>
      </div>}
    </div>
  );
}
